import { MenuItem } from 'primeng/api';
import { TableColumnConfig } from '../interfaces/table-builder.interface';

type TableRow = {
    selected?: boolean;
    [key: string]: any;
};

type TableSelectionEvent = {
    selectedRows: TableRow[];
    allSelected: boolean;
};

export type ActionButtonConfig = {
    columnLabel: string;
    buttonType: 'button' | 'splitButton';
    buttonLabel: string;
    labelMapping?: string;
    icon?: string;
    menuItems?: MenuItem[];
    outlined?: boolean;
    iconPosition?: 'left' | 'right';
    cellClass?: string | string[];
    cellStyle?: { [key: string]: any };
    text?: boolean;
    disabled?: boolean;
    frozen?: boolean;
};

export type TableConfig = {
    tableConfiguration: TableColumnConfig[];
    data: TableRow[];
    reorderable: boolean;
    selectable: boolean;
    pageSizeOptions: { value: number; viewValue: string }[];
    totalItems: number;
    currentPage: number;
    searchQuery: string;
    enableAction: boolean;
    enableBulkAction: boolean;
    bulkButtonConfig?: ActionButtonConfig;
    actionButtonConfig?: ActionButtonConfig;
    clearAllButtonConfig?: ActionButtonConfig;
};

export class TableConfigBuilder {
    private config: TableConfig;

    private label: string = '';

    // Arrays to hold modifier functions
    private columnModifiers: ((column: TableColumnConfig) => Promise<TableColumnConfig> | TableColumnConfig)[] = [];
    private rowModifiers: ((row: TableRow) => Promise<TableRow> | TableRow)[] = [];

    constructor(label?: string) {
        this.label = label ? label : '';
        this.config = {
            tableConfiguration: [],
            data: [],
            reorderable: false,
            selectable: false,
            pageSizeOptions: [],
            totalItems: 0,
            currentPage: 1,
            searchQuery: '',
            enableAction: false,
            enableBulkAction: false,
        };
    }

    setLabel(label: string): this {
        this.label = label;
        return this;
    }

    getLabel(): string {
        return this.label;
    }

    addColumn(column: TableColumnConfig): this {
        if (!column) {
            return this;
        }

        /**
         * {
            hideable: false,
            sort: false,
            name: 'cid',
            label: 'CID',
            mapping: 'cid',
            mappingType: 'case_instance',
        },
         */
        if (!column.columnName) {
            column.columnName = column['label'] ? column['label'] : column['name'];
        }

        if (!column.mapping) {
            column.mapping = column['name'];
        }

        if (column.hasOwnProperty('hideable')) {
            column.hide = column['hideable'] || false;
        }

        if (column.hasOwnProperty('sort')) {
            column.enableSort = column['sort'] || false;
        }
        if (column.hasOwnProperty('frozen')) {
            column.frozen = column['frozen'] || false;
        }
        if (column.hasOwnProperty('alignFrozen')) {
            column.alignFrozen = column['alignFrozen'] || 'left';
        }

        this.config.tableConfiguration.push(column);
        return this;
    }

    addColumns(columns: TableColumnConfig[]): this {
        if (!Array.isArray(columns)) {
            return this;
        }

        for (const column of columns) {
            this.addColumn(column);
        }
        return this;
    }

    addRow(row: TableRow): this {
        this.config.data.push(row);
        this.config.totalItems = this.config.data.length;
        return this;
    }

    addRows(rows: TableRow[]): this {
        if (!Array.isArray(rows)) {
            return this;
        }

        if (!Array.isArray(this.config.data)) {
            this.config.data = [];
        }
        this.config.data = [...this.config.data, ...rows];
        this.config.totalItems = this.config.data.length;
        return this;
    }

    getRows(): TableRow[] {
        return this.config.data;
    }

    editRow(index: number, row: TableRow): this {
        if (index < 0 || index >= this.config.data.length) {
            return this;
        }

        this.config.data[index] = row;
        return this;
    }

    setReorderable(value: boolean): this {
        if (typeof value !== 'boolean') {
            return this;
        }
        this.config.reorderable = value;
        return this;
    }

    setSelectable(value: boolean): this {
        if (typeof value !== 'boolean') {
            return this;
        }
        this.config.selectable = value;
        return this;
    }

    setPageSizeOptions(options: { value: number; viewValue: string }[]): this {
        if (!Array.isArray(options)) {
            return this;
        }
        // set default value if invalid options
        for (let i = 0; i < options.length; i++) {
            if (typeof options[i].value !== 'number' || typeof options[i].viewValue !== 'string') {
                options[i].value = 10;
                options[i].viewValue = '10';
            }
        }
        this.config.pageSizeOptions = options;
        return this;
    }

    setBulkButtonConfig(config: ActionButtonConfig): this {
        if (!config) {
            return this;
        }
        // set default values
        if (!config.buttonType) {
            config.buttonType = 'button';
        }
        if (!config.buttonLabel) {
            config.buttonLabel = 'Apply';
        }
        this.config.bulkButtonConfig = config;
        return this;
    }

    setActionButtonConfig(config: ActionButtonConfig): this {
        if (!config) {
            return this;
        }
        // set default values
        if (!config.buttonType) {
            config.buttonType = 'button';
        }
        if (!config.buttonLabel) {
            config.buttonLabel = 'Apply';
        }
        this.config.actionButtonConfig = config;
        return this;
    }

    setClearAllButtonConfig(config: ActionButtonConfig) {
        if (!config) {
            return this;
        }
        // set default values
        if (!config.buttonType) {
            config.buttonType = 'button';
        }
        if (!config.buttonLabel) {
            config.buttonLabel = 'Clear Selections';
        }
        this.config.clearAllButtonConfig = config;
        return this;
    }

    setSearchQuery(query: string): this {
        this.config.searchQuery = query;
        return this;
    }

    setCurrentPage(page: number): this {
        this.config.currentPage = page;
        return this;
    }

    // Register a column modifier
    addColumnModifier(modifier: (column: TableColumnConfig) => Promise<TableColumnConfig> | TableColumnConfig): this {
        this.columnModifiers.push(modifier);
        return this;
    }

    // Register a row modifier
    addRowModifier(modifier: (row: TableRow) => Promise<TableRow> | TableRow): this {
        this.rowModifiers.push(modifier);
        return this;
    }

    // Apply column modifiers to a copy
    private async getModifiedColumns(): Promise<TableColumnConfig[]> {
        const modifiedColumns = [...this.config.tableConfiguration]; // Create a copy
        for (let i = 0; i < modifiedColumns.length; i++) {
            for (const modifier of this.columnModifiers) {
                modifiedColumns[i] = await modifier(modifiedColumns[i]);
            }
        }
        return modifiedColumns;
    }

    // Apply row modifiers to a copy
    private async getModifiedRows(): Promise<TableRow[]> {
        const modifiedRows = [...this.config.data]; // Create a copy
        for (let i = 0; i < modifiedRows.length; i++) {
            for (const modifier of this.rowModifiers) {
                modifiedRows[i] = await modifier(modifiedRows[i]);
            }
        }
        return modifiedRows;
    }

    // Generate the latest view without modifying the actual data
    async view(): Promise<{ tableConfiguration: TableColumnConfig[]; data: TableRow[] }> {
        const modifiedColumns = await this.getModifiedColumns();
        const modifiedRows = await this.getModifiedRows();
        return {
            tableConfiguration: modifiedColumns,
            data: modifiedRows,
        };
    }

    build(): TableConfig {
        return this.config;
    }
}
